'use client';

import { faClose } from '@fortawesome/pro-regular-svg-icons';
import { useEffect, useRef, useState } from 'react';

import { DeprecatedDropdown } from '../DeprecatedDropdown';
import { Icon } from '../Icon/Icon';

export type DeprecatedOptionType = {
  id: number | string;
  label: string;
  imageUrl?: string;
  selected?: boolean;
};

type ComboDropdownProps = {
  options: DeprecatedOptionType[];
  onSelect: (option: DeprecatedOptionType) => void;
  onRemove: (option: DeprecatedOptionType) => void;
  onRemoveAll?: () => void;
  selectedOptions: DeprecatedOptionType[];
  renderTrigger: (props: { className: string }) => React.ReactNode;
  placeholder?: string;
  alwaysShowOptions?: boolean;
  ImageComponent?: ({
    src,
    alt,
    className,
  }: {
    src: string;
    alt: string;
    className: string;
  }) => JSX.Element;
  renderOption?: (
    option: DeprecatedOptionType,
    { active, selected }: { active: boolean; selected: boolean },
  ) => React.ReactNode;
  maxSelections?: number;
  maxRenderAmount?: number;
  asPortal?: boolean;
  align?: 'start' | 'end' | 'center';
  zIndex?: number;
  defaultOpen?: boolean;
  avoidCollisions?: boolean;
  collisionPadding?: number;
  onOpenChange?: (open: boolean) => void;
};

function cn(...classes: Array<string | false>) {
  return classes.filter(Boolean).join(' ');
}

export const DeprecatedComboDropdown: React.FC<ComboDropdownProps> = ({
  options,
  onSelect,
  onRemove,
  onRemoveAll,
  selectedOptions,
  ImageComponent,
  renderTrigger,
  renderOption,
  placeholder,
  maxSelections,
  maxRenderAmount,
  alwaysShowOptions = false,
  asPortal = true,
  align = 'center',
  zIndex = 0,
  defaultOpen = false,
  avoidCollisions,
  collisionPadding,
  ...props
}) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [displayedTags, setDisplayedTags] = useState<DeprecatedOptionType[]>([]);

  useEffect(() => {
    if (maxRenderAmount) {
      // If maxRenderAmount is set, only render the first n options
      setDisplayedTags(options.slice(0, maxRenderAmount));
    } else {
      setDisplayedTags(options);
    }
  }, [options, maxRenderAmount]);

  useEffect(() => {
    if (query !== '') {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(query.toLowerCase()),
      );

      if (maxRenderAmount) {
        setDisplayedTags(filtered.slice(0, maxRenderAmount));
      } else {
        setDisplayedTags(filtered);
      }
    } else {
      setDisplayedTags(options.slice(0, maxRenderAmount || options.length));
    }
  }, [query, options, maxRenderAmount]);

  const optionsToShow = alwaysShowOptions
    ? options.filter((option) => !selectedOptions.find((s) => s?.id === option.id))
    : displayedTags.filter((option) => !selectedOptions.find((s) => s?.id === option.id));

  useEffect(() => {
    optionRefs.current = optionRefs.current.slice(0, optionsToShow.length);
  }, [optionsToShow]);

  const handleSelect = (option: DeprecatedOptionType) => {
    onSelect(option);
    inputRef.current?.focus();
    setActiveIndex(0);

    // Clear query after selection if multiple selections are allowed
    if (maxSelections && maxSelections > 1) {
      setQuery('');
    }
  };

  const handleRemove = (option?: DeprecatedOptionType) => {
    if (!option) {
      selectedOptions.forEach((o) => onRemove(o));
      return;
    }
    onRemove(option);
  };

  const handleRemoveLast = () => {
    const lastOption = selectedOptions[selectedOptions.length - 1];

    handleRemove(lastOption);
  };

  const defaultRenderOption = (
    option: DeprecatedOptionType,
    { selected }: { selected: boolean },
  ) => (
    <div className='inline-flex items-center rounded-[4px]'>
      {option?.imageUrl && ImageComponent && (
        <ImageComponent
          src={option?.imageUrl}
          alt=''
          className='h-6 w-6 flex-shrink-0 rounded-full'
        />
      )}
      {option?.imageUrl && !ImageComponent && (
        <img src={option?.imageUrl} alt='' className='h-6 w-6 flex-shrink-0 rounded-full' />
      )}
      <span className={cn('ml-3 truncate', selected && 'font-semibold')}>{option?.label}</span>
    </div>
  );

  const renderOptionOrDefault = renderOption || defaultRenderOption;

  return (
    <DeprecatedDropdown
      renderTrigger={renderTrigger}
      variant='primary'
      asPortal={asPortal}
      align={align}
      zIndex={zIndex}
      defaultOpen={defaultOpen}
      avoidCollisions={avoidCollisions}
      collisionPadding={collisionPadding}
      {...props}
    >
      <div className='relative w-full rounded-md bg-white'>
        <div
          className={cn(
            // negative margins & calc width account for discrepancy in border width betwewn browser and figma
            '-mx-0.5 -my-0.5 flex w-[100%_+_4px] max-w-[236px] flex-col rounded-[5px] border-2 border-indigo-400 px-2',
            selectedOptions?.length > 0 && 'p-2',
            selectedOptions?.length === maxSelections && 'mx-0 mt-0 border-none',
          )}
        >
          <div className='flex gap-x-1.5'>
            <div className='max-w-full flex-1 overflow-hidden'>
              {selectedOptions?.length > 0 &&
                selectedOptions
                  ?.filter((option) => !!options && !!option?.label)
                  ?.map((option, index) => (
                    <div key={option?.id || index} className='flex h-[30px] flex-row items-center'>
                      {renderOptionOrDefault(option, {
                        active: false,
                        selected: true,
                      })}
                      <button
                        type='button'
                        className='ml-2 flex h-5 w-5 flex-none cursor-pointer items-center justify-center rounded-full bg-transparent text-slate-400 hover:bg-slate-200 hover:text-slate-400 focus:bg-slate-200 focus:text-white focus:outline-none'
                        onClick={() => handleRemove(option)}
                      >
                        <span className='sr-only'>Remove {option?.label} Selection</span>

                        <Icon icon={faClose} className='size-3.5' />
                      </button>
                    </div>
                  ))}
            </div>
            {!!onRemoveAll && selectedOptions?.length > 1 && (
              <button
                title='Clear All Selected'
                aria-label='Clear All Selected'
                className='flex h-5 w-5 flex-none cursor-pointer items-center justify-center rounded-full bg-slate-200 text-slate-500 hover:bg-slate-300 focus:bg-slate-200 focus:text-white focus:outline-none'
                onClick={() => onRemoveAll()}
              >
                <Icon icon={faClose} className='size-3.5' />
              </button>
            )}
          </div>
          {selectedOptions?.length !== maxSelections && (
            <div className='relative flex w-full flex-row p-0'>
              <input
                className={cn(
                  'w-full border-0 border-none bg-transparent px-[6px] py-2 outline-none',
                )}
                ref={inputRef}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && query === '') {
                    handleRemoveLast();
                    return;
                  }

                  if (e.key === 'Enter') {
                    handleSelect(optionsToShow[0]);
                    return;
                  }

                  if (e.key === 'ArrowDown' || e.key === 'Tab') {
                    e.preventDefault();
                    inputRef.current?.blur();
                    setActiveIndex(0);

                    optionRefs.current[activeIndex]?.focus();
                    listContainerRef.current?.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  }

                  if (e.key === 'Escape') {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
                onChange={(event) => {
                  setQuery(event.target.value);
                  setActiveIndex(0);
                  listContainerRef.current?.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  });
                }}
                value={query}
                placeholder={selectedOptions.length < 1 ? placeholder : undefined}
              />
            </div>
          )}
        </div>
        <div className='w-[232px]' />
        {optionsToShow.length > 0 && selectedOptions?.length !== maxSelections && (
          <div
            className=' z-10 mt-1 max-h-40 w-[232px] overflow-auto overflow-y-scroll overflow-ellipsis  rounded-md py-1 text-base focus:outline-none sm:text-sm'
            ref={listContainerRef}
          >
            {optionsToShow.map((option, index) => (
              <div
                key={option?.id}
                className={cn(
                  'relative flex h-9 cursor-pointer select-none list-none items-center rounded py-1.5 pl-2 pr-9 text-slate-900 outline-none hover:bg-indigo-50',
                  index === activeIndex && 'bg-indigo-50',
                )}
                onClick={() => handleSelect(option)}
                tabIndex={0}
                ref={(ref) => {
                  optionRefs.current[index] = ref;
                }}
                role='button'
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    handleSelect(option);
                  }

                  if (e.key === 'ArrowDown' || e.key === 'Tab') {
                    e.preventDefault();
                    optionRefs.current[activeIndex]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                    if (activeIndex < optionsToShow.length - 1) {
                      setActiveIndex((i) => i);
                      optionRefs.current[activeIndex]?.focus();
                      listContainerRef.current?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                      });
                    } else {
                      setActiveIndex(0);
                      inputRef.current?.focus();
                      return;
                    }
                    setActiveIndex((oldIndex) => {
                      const newIndex = oldIndex === optionsToShow.length - 1 ? 0 : oldIndex + 1;

                      optionRefs.current[newIndex]?.focus();
                      return newIndex;
                    });
                  }

                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    optionRefs.current[activeIndex]?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                    if (activeIndex === 0) {
                      inputRef.current?.focus();
                      // scroll to top

                      return;
                    }
                    setActiveIndex((oldIndex) => {
                      const newIndex = oldIndex === 0 ? optionsToShow.length - 1 : oldIndex - 1;

                      optionRefs.current[newIndex]?.focus();
                      return newIndex;
                    });
                  }
                }}
              >
                {renderOptionOrDefault(option, {
                  active: index === 0,
                  selected: false,
                })}
              </div>
            ))}
          </div>
        )}
        {optionsToShow.length === 0 && selectedOptions?.length === 0 && (
          <div className=' z-10 mt-1 max-h-40 w-[232px] overflow-auto overflow-y-scroll overflow-ellipsis  rounded-md py-1 text-base focus:outline-none sm:text-sm'>
            <div
              className={cn(
                'relative cursor-default select-none list-none rounded-[4px] py-1.5 pl-2  pr-9 text-slate-900 outline-none hover:bg-indigo-50',
              )}
            >
              No results found
            </div>
          </div>
        )}
      </div>
    </DeprecatedDropdown>
  );
};
